/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @next/next/no-img-element */
import {
  ChangeEventHandler,
  FC,
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { createPortal } from 'react-dom';

import {
  useEventListener,
  useOnClickOutside,
  useDebounceValue,
} from 'usehooks-ts';
import { CSSProperties, useTheme } from 'styled-components';

import { inter } from '~/common/styles/fonts';
import customAxios from '~/common/services/customAxios';
import { useToast } from '~/common/providers/toast';
import { useAuth } from '~/common/providers/auth';
import { vipUsers } from '~/common/constants/vip-users';

import {
  IDropdownOption,
  IDropdownProps,
  TDropdownPosition,
  TDropdownValue,
  TOnChangeDropdown,
} from './types';
import {
  Container,
  InputWrapper,
  CustomInput,
  RightIconWrapper,
  StyIconLabelContent,
  CloseIcon,
} from './styles';
import { DropdownOptions } from './DropdownOptions';
import { HelperText } from '../HelperText';
import { FormLabel } from '../FormLabel';
import { Typography } from '../../Typography';
import { Icon } from '../../Icons';

const BaseDropdown: FC<IDropdownProps> = ({
  onChange = () => null,
  withoutClear = false,
  returnFullAsyncValue = false,
  withoutValue = false,
  hideOptionLabel = false,
  filter = [],
  value: _value,
  options = [],
  placeholder = 'Selecione uma opção',
  label,
  size = 'medium',
  error,
  defaultValue,
  asyncParams = [],
  noOptionsText,
  scrollViewId = 'main-element',
  containerStyle = {},
  name,
  isDisabled,
  helperText,
  leftCustomIcon,
  leftIcon,
  width = '300px',
  variant = 'default',
  borderRadius = 'default',
  valueType = 'string',
  asyncData,
  resetListener,
}) => {
  const id = useId();
  const { user: { subscription: userType } = {} } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const [mounted, setMounted] = useState(false);
  const [portalContainer, setPortalContainer] = useState<Element | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLLabelElement>(null);
  const memorizedFirstAsyncState = useRef<IDropdownOption[]>(null);

  const [asyncOptions, setAsyncOptions] = useState<IDropdownOption[]>([]);
  const [debouncedValue, setSearchValue] = useDebounceValue('', 250);
  const [containerClientRect, setContainerClientRect] = useState<DOMRect>();
  const [inputWrapperClientRect, setInputWrapperClientRect] =
    useState<DOMRect>();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [value, setValue] = useState<TDropdownValue>('');
  const [position, setPosition] = useState<TDropdownPosition>('bottom');
  const [asyncValueOption, setAsyncValueOption] = useState<IDropdownOption>();

  const handleSetClientRect = (): void => {
    const _containerClientRect = containerRef.current?.getBoundingClientRect();
    const _inputWrapperClientRect =
      inputWrapperRef.current?.getBoundingClientRect();
    if (!_containerClientRect || !_inputWrapperClientRect) return;

    const isAtScreen =
      _inputWrapperClientRect.bottom + 216 <= window.innerHeight;

    setContainerClientRect(_containerClientRect);
    setInputWrapperClientRect(_inputWrapperClientRect);
    setPosition(isAtScreen ? 'bottom' : 'top');
  };

  const handleSelectChange: TOnChangeDropdown = useCallback(
    (option) => {
      if (asyncData) {
        const found = asyncOptions.find((where) => where.value === option);
        setAsyncValueOption(found);

        if (returnFullAsyncValue) {
          onChange(found);
          return;
        }
      }

      onChange(option, name);
    },
    [asyncData, asyncOptions, returnFullAsyncValue, name, onChange]
  );

  const asyncLoad = useCallback(async () => {
    setAsyncOptions([]);
    try {
      const method = asyncData.method || 'post';
      const payload = {
        [asyncData.key]: debouncedValue,
        ...(asyncData.defaultParams || {}),
      };

      let response = null;
      if (method === 'post') {
        const { data } = await customAxios.post(asyncData.route, payload);
        response = data;
      } else {
        const { data } = await customAxios.get(asyncData.route, {
          params: payload,
        });

        response = data;
      }

      const toMap = asyncData.arrayResponseField
        ? response[asyncData.arrayResponseField]
        : response;

      const mapped = toMap.map(asyncData.mapper) as IDropdownOption[];
      if (!mapped.length) {
        return;
      }

      if (!memorizedFirstAsyncState.current) {
        memorizedFirstAsyncState.current = mapped;
      }

      if (!mapped || !mapped.length) {
        setAsyncOptions(memorizedFirstAsyncState.current || []);
      } else {
        setAsyncOptions((prevState) => [
          ...prevState,
          ...mapped.filter(
            (opt) => !prevState.find((prv) => prv.value === opt.value)
          ),
        ]);
      }
    } catch {
      if (!userType || !vipUsers.includes(userType)) {
        return;
      }

      showToast({
        type: 'error',
        title: 'Erro',
        description:
          asyncData.errorMessage || 'Houve um erro ao carregar as opções',
      });
    }
  }, [asyncData, debouncedValue, userType, showToast, ...(asyncParams || [])]);

  useEffect(() => {
    if (asyncData) {
      asyncLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, ...asyncParams]);

  useEffect(() => {
    setMounted(true);
    setPortalContainer(document.getElementById('portal-root'));
    handleSetClientRect();
  }, []);

  useEffect(() => {
    setValue(_value);
  }, [_value]);

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
    } else if (asyncData) {
      setAsyncValueOption(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const openDropdown = (): void => {
    if (isDropdownVisible || isDisabled) return;

    handleSetClientRect();
    setIsDropdownVisible(true);
    inputRef.current?.focus();
  };

  const closeDropdown = (): void => {
    if (!isDropdownVisible) return;
    setIsDropdownVisible(false);
    setSearchValue('');

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleClickSelect = (): void => {
    isDropdownVisible ? closeDropdown() : openDropdown();
  };

  const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearchValue(event.target.value);
  };

  const finalOptions = useMemo(() => {
    const returning = asyncData
      ? asyncOptions.length
        ? asyncOptions
        : memorizedFirstAsyncState.current || []
      : options;

    if (filter.length) {
      const filtered = returning.filter(
        (option) => !filter.includes(String(option.value))
      );

      return filtered;
    }

    return returning;
  }, [asyncData, asyncOptions, filter, options]);

  const formattedOptions = useMemo(() => {
    if (debouncedValue) {
      const normalizeText = (text: string) =>
        text.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      const normalizedSearch = normalizeText(debouncedValue);

      return finalOptions.filter(
        (option) =>
          normalizeText(option.label ?? '').includes(normalizedSearch) ||
          normalizeText(option.value?.toString() ?? '').includes(normalizedSearch)
      );
    }

    return finalOptions;
  }, [finalOptions, debouncedValue]);

  const optionValue: IDropdownOption | null = useMemo(() => {
    if (withoutValue) return null;

    if (asyncData && asyncValueOption) {
      return asyncValueOption;
    }

    if (!value) return null;

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return finalOptions?.find((option) => option.value === value);
    }

    return (value as IDropdownOption) || null;
  }, [asyncData, asyncValueOption, withoutValue, value, finalOptions]);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.value = optionValue?.label || '';
  }, [optionValue]);

  const shouldRenderImageOrIcon = useMemo(() => {
    return (
      ['icon', 'image-text'].includes(valueType) &&
      (optionValue?.leftCustomIcon ||
        optionValue?.leftIcon ||
        optionValue?.image) &&
      !isDropdownVisible
    );
  }, [valueType, optionValue, isDropdownVisible]);

  useOnClickOutside(dropdownListRef, closeDropdown);
  useEventListener('resize', handleSetClientRect);
  useEventListener('scroll', closeDropdown, {
    ...(mounted &&
      isDropdownVisible &&
      portalContainer && { current: document.getElementById(scrollViewId) }),
  });

  const customStyle: CSSProperties = {};

  if (isDisabled) {
    customStyle.pointerEvents = 'none';
  }

  if (width) {
    customStyle.width = width;
  }

  useEffect(() => {
    if (resetListener) {
      if (asyncData) {
        asyncLoad();
      }
    }
  }, [resetListener, asyncData]);

  return (
    <Container htmlFor={id} ref={containerRef} style={customStyle}>
      {!!label && <FormLabel {...label} isDisabled={isDisabled} />}

      <InputWrapper
        $hasError={!!error}
        $isDisabled={isDisabled}
        $size={size}
        style={containerStyle}
        $width={width}
        ref={inputWrapperRef}
        $variant={variant}
        $borderRadius={borderRadius}
        {...(!isDropdownVisible && { onClick: handleClickSelect })}
      >
        {(variant !== 'compact' ||
          shouldRenderImageOrIcon ||
          (!optionValue && !isDropdownVisible)) && (
          <>
            {shouldRenderImageOrIcon ? (
              <>
                {optionValue.image ? (
                  <>
                    <img
                      src={
                        typeof optionValue.image === 'string'
                          ? optionValue.image
                          : optionValue.image?.src
                      }
                      alt={optionValue.label}
                      style={{
                        width: '30px',
                        height: '30px',
                      }}
                    />
                    <Typography.Label
                      size="small"
                      color={
                        optionValue.isDisabled ? 'neutral.500' : 'neutral.400'
                      }
                    >
                      {optionValue.label}
                    </Typography.Label>
                  </>
                ) : (
                  <>
                    {optionValue.leftCustomIcon ? (
                      <StyIconLabelContent>
                        <>{optionValue.leftCustomIcon}</>
                        {!hideOptionLabel && (
                          <Typography.Label
                            size="small"
                            color={
                              optionValue.isDisabled
                                ? 'neutral.500'
                                : 'neutral.400'
                            }
                          >
                            {optionValue.label}
                          </Typography.Label>
                        )}
                      </StyIconLabelContent>
                    ) : (
                      <StyIconLabelContent>
                        <Icon
                          type={optionValue.leftIcon}
                          size={20}
                          color={
                            isDisabled ? theme.neutral[600] : theme.neutral[500]
                          }
                        />
                        {!hideOptionLabel && (
                          <Typography.Label
                            size="small"
                            color={
                              optionValue.isDisabled
                                ? 'neutral.500'
                                : 'neutral.400'
                            }
                          >
                            {optionValue.label}
                          </Typography.Label>
                        )}
                      </StyIconLabelContent>
                    )}
                  </>
                )}
              </>
            ) : leftCustomIcon ? (
              <>{leftCustomIcon}</>
            ) : leftIcon ? (
              <Icon
                type={leftIcon}
                size={20}
                color={isDisabled ? theme.neutral[600] : theme.neutral[500]}
              />
            ) : null}
          </>
        )}

        {!shouldRenderImageOrIcon &&
          (variant === 'compact'
            ? isDropdownVisible || (!leftCustomIcon && !leftIcon)
            : true) && (
            <CustomInput
              disabled={isDisabled}
              name={name}
              id={id}
              ref={inputRef}
              onChange={handleChangeSearch}
              autoComplete="new-password"
              autoCorrect="off"
              type="text"
              $variant={variant}
              {...(variant !== 'compact' && { placeholder })}
              {...(!isDropdownVisible && { onClick: handleClickSelect })}
              {...(valueType === 'string' &&
                !!optionValue &&
                !isDropdownVisible && { value: optionValue?.label })}
              className={inter.className}
            />
          )}

        {!!optionValue && variant !== 'compact' && (
          <>
            {!withoutClear && (
              <CloseIcon
                type="button"
                onClick={(ev) => {
                  ev.stopPropagation();
                  handleSelectChange(null);
                  ev.stopPropagation();
                }}
              >
                <Icon type="close-line" size={20} color={theme.neutral[500]} />
              </CloseIcon>
            )}
          </>
        )}

        <RightIconWrapper $isDropdownVisible={isDropdownVisible}>
          <Icon
            type="arrow-down-s-line"
            size={20}
            color={isDisabled ? theme.neutral[600] : theme.neutral[500]}
          />
        </RightIconWrapper>

        {mounted &&
          isDropdownVisible &&
          portalContainer &&
          createPortal(
            <DropdownOptions
              containerClientRect={containerClientRect}
              inputWrapperClientRect={inputWrapperClientRect}
              position={position}
              isDropdownVisible={isDropdownVisible}
              size={size}
              borderRadius={borderRadius}
              options={formattedOptions}
              isDisabled={isDisabled}
              onCloseDropdown={closeDropdown}
              noOptionsText={noOptionsText}
              leftCustomIcon={leftCustomIcon}
              leftIcon={leftIcon}
              variant={variant}
              selectedOption={optionValue}
              onSelectOption={handleSelectChange}
            />,
            portalContainer
          )}
      </InputWrapper>

      {(!!helperText || !!error) && variant !== 'compact' && (
        <HelperText isDisabled={isDisabled} isError={!!error}>
          {error || helperText}
        </HelperText>
      )}
    </Container>
  );
};

export const Dropdown = memo(BaseDropdown);
